//@flow

import { injectError } from 'common/redux/actions/error';
import { LocusApiModel, ApiError } from 'common/modules/api';
import ImmutableModel from 'common/models/flow/ImmutableModel';
import JSONPatchUtils from 'common/utils/JSONPatchUtils';

import type { List } from 'immutable';
import type { Dispatch } from 'redux';

export type FetchEntitiesOptionsType = {
    processResponse?: any => Array<Object>,
    onSuccess?: (Array<Object>) => void,
    onFailure?: Error => void,
    dontInjectError?: boolean,
    dontInjectSuccess?: boolean,
    dontShowLoader?: boolean
};

export type SingleEntityOptionsType = {
    processResponse?: any => Object,
    onSuccess?: Object => void,
    onFailure?: Error => void,
    dontInjectError?: boolean,
    getExtraPatchOptions?: () => Array<Object>
};

export type ActionTypes = {
    FETCH_ENTITIES_START: string,
    FETCH_ENTITIES_SUCCESS: string,
    FETCH_ENTITIES_FAILURE: string,
    ADD_ENTITY_START: string,
    ADD_ENTITY_SUCCESS: string,
    ADD_ENTITY_FAILURE: string,
    REFRESH_ENTITY_START: string,
    REFRESH_ENTITY_SUCCESS: string,
    REFRESH_ENTITY_FAILURE: string,
    CLEAR_DATA: string,
    FETCH_ENTITY_START: string,
    FETCH_ENTITY_SUCCESS: string,
    FETCH_ENTITY_FAILURE: string
};

export default class BaseActions<TEntity: ImmutableModel, TParams> {
    api: LocusApiModel;

    types: ActionTypes = {
        FETCH_ENTITIES_START: 'FETCH_ENTITIES_START',
        FETCH_ENTITIES_SUCCESS: 'FETCH_ENTITIES_SUCCESS',
        FETCH_ENTITIES_FAILURE: 'FETCH_ENTITIES_FAILURE',
        ADD_ENTITY_START: 'ADD_ENTITY_START',
        ADD_ENTITY_SUCCESS: 'ADD_ENTITY_SUCCESS',
        ADD_ENTITY_FAILURE: 'ADD_ENTITY_FAILURE',
        REFRESH_ENTITY_START: 'REFRESH_ENTITY_START',
        REFRESH_ENTITY_SUCCESS: 'REFRESH_ENTITY_SUCCESS',
        REFRESH_ENTITY_FAILURE: 'REFRESH_ENTITY_FAILURE',
        CLEAR_DATA: 'CLEAR_DATA',
        FETCH_ENTITY_START: 'FETCH_ENTITY_START',
        FETCH_ENTITY_SUCCESS: 'FETCH_ENTITY_SUCCESS',
        FETCH_ENTITY_FAILURE: 'FETCH_ENTITY_FAILURE'
    };

    constructor(TAG: string, api: Model) {
        this.api = api;
        this.tag = TAG;
        Object.keys(this.types).forEach(actionType => {
            this.types[actionType] = `${this.types[actionType]}_${TAG}`;
        });
    }

    getTypes() {
        return this.types;
    }

    handleError(
        error: Error,
        options: { onFailure?: Error => void, dontInjectError?: boolean },
        dispatch: Dispatch<*>
    ) {
        const { onFailure, dontInjectError } = options;
        onFailure && onFailure(error);
        if (error instanceof ApiError) {
            if (!dontInjectError) {
                dispatch(injectError(error));
            }
            return;
        }
        throw error;
    }

    fetchEntities = (
        params: TParams,
        options: FetchEntitiesOptionsType = {}
    ) => {
        const { processResponse, onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitFetchEntitiesStart(params));
            this.api
                .fetch(params)
                .then((response: any) => {
                    let entities = null;
                    if (processResponse) {
                        entities = processResponse(response);
                    } else {
                        entities = (response: Array<Object>);
                    }
                    onSuccess && onSuccess(entities);
                    dispatch(this.injectEntities(params, entities));
                })
                .catch((error: Error) => {
                    dispatch(this.emitFetchEntitiesFailure(params, error));
                    this.handleError(error, options, dispatch);
                });
        };
    };

    addEntity = (
        params: TParams,
        entity: TEntity,
        options: SingleEntityOptionsType = {}
    ) => {
        const { processResponse, onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitAddEntityStart(params));
            this.api
                .put(params, entity.toJS())
                .then((response: any) => {
                    let entity = null;
                    if (processResponse) {
                        entity = processResponse(response);
                    } else {
                        entity = (response: Object);
                    }
                    onSuccess && onSuccess(entity);
                    dispatch(this.injectEntity(params, entity));
                    return response;
                })
                .catch((error: Error) => {
                    dispatch(this.emitAddEntityFailure(params, error));
                    this.handleError(error, options, dispatch);
                });
        };
    };

    addEntities = (
        params: TParams,
        entities: List<TEntity>,
        options: SingleEntityOptionsType = {}
    ) => {
        const { onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            this.api
                .post(params, entities.toJS())
                .then((response: any) => {
                    onSuccess && onSuccess(response);
                    return response;
                })
                .catch((error: Error) => {
                    this.handleError(error, options, dispatch);
                });
        };
    };

    patchEntity = (
        params: TParams,
        oldEntity: TEntity,
        newEntity: TEntity,
        options: SingleEntityOptionsType = {}
    ) => {
        const { processResponse, onSuccess, getExtraPatchOptions } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitAddEntityStart(params));
            let jsonDiff = JSONPatchUtils.diff(
                oldEntity.toJS(),
                newEntity.toJS()
            );
            const extraDiff =
                getExtraPatchOptions?.({
                    jsonDiff,
                    entity: newEntity
                }) || [];
            jsonDiff = [
                ...jsonDiff,
                {
                    op: 'test',
                    path: '/version',
                    value: newEntity.toJS().version
                },
                ...extraDiff
            ];
            this.api
                .patch(params, jsonDiff)
                .then((response: any) => {
                    let entity = null;
                    if (processResponse) {
                        entity = processResponse(response);
                    } else {
                        entity = (response: Object);
                    }
                    onSuccess && onSuccess(entity);
                    dispatch(this.injectEntity(params, entity));
                    return response;
                })
                .catch((error: Error) => {
                    dispatch(this.emitAddEntityFailure(params, error));
                    this.handleError(error, options, dispatch);
                });
        };
    };

    refreshEntity = (
        params: TParams,
        options: SingleEntityOptionsType = {}
    ) => {
        const { processResponse, onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitRefreshEntityStart(params));
            this.api
                .fetch(params)
                .then((response: any) => {
                    let entity = null;
                    if (processResponse) {
                        entity = processResponse(response);
                    } else {
                        entity = (response: Object);
                    }
                    onSuccess && onSuccess(entity);
                    dispatch(
                        this.injectEntity(
                            params,
                            entity,
                            this.types.REFRESH_ENTITY_SUCCESS
                        )
                    );
                    return response;
                })
                .catch((error: Error) => {
                    dispatch(this.emitRefreshEntityFailure(params, error));
                    this.handleError(error, options, dispatch);
                });
        };
    };

    emitFetchEntitiesStart(
        params: TParams,
        body: Object,
        options: Object = {}
    ) {
        return {
            type: this.types.FETCH_ENTITIES_START,
            params,
            body,
            options
        };
    }

    injectEntities(
        params: TParams,
        entities: Array<Object>,
        body: Object,
        options: Object = {}
    ) {
        return {
            type: this.types.FETCH_ENTITIES_SUCCESS,
            params,
            entities,
            body,
            options
        };
    }

    emitFetchEntitiesFailure(
        params: TParams,
        error: Error,
        body: Object,
        options: Object = {}
    ) {
        return {
            type: this.types.FETCH_ENTITIES_FAILURE,
            params,
            error,
            body,
            options
        };
    }

    emitAddEntityStart(params: TParams) {
        return {
            type: this.types.ADD_ENTITY_START,
            params
        };
    }

    emitRefreshEntityStart(params: TParams) {
        return {
            type: this.types.REFRESH_ENTITY_START,
            params
        };
    }

    injectEntity(
        params: TParams,
        entity: Object,
        action: $Values<ActionTypes> = this.types.ADD_ENTITY_SUCCESS
    ) {
        return {
            type: action,
            params,
            entity
        };
    }

    emitAddEntityFailure(params: TParams, error: Error) {
        return {
            type: this.types.ADD_ENTITY_FAILURE,
            params,
            error
        };
    }

    emitRefreshEntityFailure(params: TParams, error: Error) {
        return {
            type: this.types.REFRESH_ENTITY_FAILURE,
            params,
            error
        };
    }

    clearData = () => {
        return {
            type: this.types.CLEAR_DATA
        };
    };
}
